import type { IPlayersTeamSquad, InningScorecard } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { batterName, bowlerName, econOf, fullName, isDismissed, isLiveInning, norm, oversText, roleTags, srOf, teamByName } from "./helpers";
import type { MatchInfo } from "./helpers";
import { Empty, InningSeg, PlayerLink } from "./parts";

type Props = { match?: MatchInfo; innings: InningScorecard[]; liveIndex: number; matchLive: boolean; sel: number | null; setSel: (i: number) => void; squadOne?: IPlayersTeamSquad; squadTwo?: IPlayersTeamSquad };

const ScorecardTab = ({ match, innings, liveIndex, matchLive, squadOne, squadTwo, sel, setSel }: Props) => {
    if (!innings.length) return <Empty>The scorecard will appear once the first ball is bowled.</Empty>;
  const idx = Math.min(sel ?? liveIndex, innings.length - 1);
  const inn = innings[idx];
  const live = matchLive && isLiveInning(inn);
  const bat = teamByName(match, inn.battingTeamName);
  const bowl = teamByName(match, inn.bowlingTeamName);
  const batters = inn.battingScoreCardResponseList ?? [];
  const bowlers = inn.bowlingScoreCardResponses ?? [];
  const extrasTotal = (inn.wide ?? 0) + (inn.noBall ?? 0) + (inn.bye ?? 0) + (inn.legBye ?? 0) + (inn.penalty ?? 0);
  const balls = (inn.over ?? 0) * 6 + (inn.ball ?? 0);
  const rr = inn.currentRunrate != null && Number(inn.currentRunrate) > 0 ? Number(inn.currentRunrate).toFixed(2) : balls ? ((inn.runs * 6) / balls).toFixed(2) : "-";
  const squad = norm(inn.battingTeamName) === norm(match?.teamOne?.teamName) ? squadOne : squadTwo;
  const batted = new Set([...batters.map((b) => b.playerId), inn.striker?.playerId, inn.nonStriker?.playerId].filter(Boolean));
  const yet = (inn.wicket ?? 0) >= 10 ? [] : (squad?.playingSquad ?? []).filter((p) => p.playerId && !batted.has(p.playerId));
  const fow = ((inn.fallOfWickets ?? []) as unknown as (string | number)[]).filter((x) => typeof x === "string" || typeof x === "number").map(String);
  const curBowlerId = inn.currentOverBalls?.length ? inn.currentOverBalls[inn.currentOverBalls.length - 1]?.bowler?.playerId : undefined;

  return (
    <>
      <div className="toolbar">
        <InningSeg innings={innings} match={match} cur={idx} onChange={setSel} />
        {matchLive ? (
          <div className="r">
            <span style={{ fontSize: 12.5, color: "var(--muted)", fontWeight: 600 }}>Refreshes automatically</span>
          </div>
        ) : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="box">
          <div className="box-h">
            <span className={`innlbl ${live ? "lv" : ""}`}>{live ? `Innings ${idx + 1} · live` : `Innings ${idx + 1}`}</span>
            <h3>{bat.name || inn.battingTeamName} batting</h3>
            <span className="sub">
              {live ? "In progress" : "Innings complete"} · {oversText(inn)} overs
            </span>
          </div>
          <div className="tw">
            <table className="sc-t">
              <thead>
                <tr>
                  <th className="l">Batter</th>
                  <th className="l" />
                  <th>R</th>
                  <th>B</th>
                  <th>4s</th>
                  <th>6s</th>
                  <th>SR</th>
                </tr>
              </thead>
              <tbody>
                {batters.map((b) => {
                  const tags = roleTags(b.playerRole);
                  const dismissed = isDismissed(b);
                  const striker = b.status === "STRIKER";
                  const nonStriker = b.status === "NON_STRIKER";
                  return (
                    <tr key={b.id ?? b.playerId}>
                      <td className="t">
                        <PlayerLink id={b.playerId} name={batterName(b)} />
                        {tags.c ? <span className="tag">C</span> : null}
                        {tags.wk ? <span className="tag">WK</span> : null}
                        {live && striker ? <span className="crole s">STRIKER</span> : null}
                        {live && nonStriker ? <span className="crole n">NON-STRIKER</span> : null}
                      </td>
                      <td className="s">{(b.outString ?? "").trim() || (live && !striker && !nonStriker ? "" : "not out")}</td>
                      <td className="big">
                        {b.run ?? 0}
                        {dismissed ? "" : "*"}
                      </td>
                      <td>{b.balls ?? 0}</td>
                      <td>{b.fours ?? 0}</td>
                      <td>{b.six ?? 0}</td>
                      <td>{b.strikeRate != null ? Number(b.strikeRate).toFixed(1) : srOf(b.run, b.balls)}</td>
                    </tr>
                  );
                })}
                <tr className="foot">
                  <td>Extras</td>
                  <td className="s">
                    b {inn.bye ?? 0}, lb {inn.legBye ?? 0}, wd {inn.wide ?? 0}, nb {inn.noBall ?? 0}
                    {inn.penalty ? `, pen ${inn.penalty}` : ""}
                  </td>
                  <td>{extrasTotal}</td>
                  <td colSpan={4} />
                </tr>
                <tr className="tot">
                  <td>Total</td>
                  <td className="s">
                    {inn.wicket ?? 0} wickets · {oversText(inn)} overs · RR {rr}
                  </td>
                  <td>
                    <span className="n">{inn.runs ?? 0}</span>
                  </td>
                  <td colSpan={4} />
                </tr>
              </tbody>
            </table>
          </div>
          {yet.length ? (
            <div className="dnb">
              <b>{live ? "Yet to bat" : "Did not bat"}</b>
              {yet.map((p, i) => (
                <span key={p.playerId}>
                  {i ? ", " : ""}
                  <PlayerLink id={p.playerId} name={fullName(p.playerFirstName, p.playerLastName) || "-"} />
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="two">
          <div className="box">
            <div className="box-h">
              <h3>{bowl.name || inn.bowlingTeamName} bowling</h3>
            </div>
            {bowlers.length ? (
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th className="l">Bowler</th>
                      <th>O</th>
                      <th>M</th>
                      <th>R</th>
                      <th>W</th>
                      <th>Econ</th>
                      <th>Dots</th>
                      <th>Wd</th>
                      <th>Nb</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bowlers.map((b) => (
                      <tr key={b.id ?? b.playerId}>
                        <td className="t">
                          <PlayerLink id={b.playerId} name={bowlerName(b)} />
                          {live && curBowlerId && b.playerId === curBowlerId ? <span className="crole b">BOWLING</span> : null}
                        </td>
                        <td>{b.over ?? 0}</td>
                        <td>{b.maiden ?? 0}</td>
                        <td className="big">{b.runs ?? 0}</td>
                        <td className="big">{b.wickets ?? 0}</td>
                        <td>{econOf(b)}</td>
                        <td>{b.dotBall ?? 0}</td>
                        <td>{b.wide ?? 0}</td>
                        <td>{b.noBall ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty>No bowling figures yet.</Empty>
            )}
          </div>
          <div className="box">
            <div className="box-h">
              <h3>Fall of wickets</h3>
            </div>
            {fow.length ? (
              <ul className="fow">
                {fow.map((f, i) => (
                  <li key={`${f}-${i}`} style={{ gridTemplateColumns: "minmax(0,1fr)" }}>
                    <span className="w">{f}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No wickets have fallen yet.</Empty>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ScorecardTab;
